import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import ErrorBoundary from './components/ErrorBoundary';
import RetreatLanding from './components/RetreatLanding';
import Waitlist from './components/Waitlist';
import Scholarship from './components/Scholarship';
import Payment from './components/Payment';
import AdminLogin from './components/AdminLogin';
import AttendeeLogin from './components/AttendeeLogin';
import { ForgotPassword, ResetPassword } from './components/PasswordReset';
import AcceptInvite from './components/AcceptInvite';
import AdminLayout from './components/retreat/AdminLayout';
import RetreatDashboard from './components/retreat/RetreatDashboard';
import AttendeeManagement from './components/retreat/AttendeeManagement';
import RoomAssignment from './components/retreat/RoomAssignment';
import ReservationsPage from './components/retreat/ReservationsPage';
import ArchivePage from './components/retreat/ArchivePage';
import Reserve from './components/Reserve';
import ReservationStatus from './components/ReservationStatus';
import SurveyPage from './components/SurveyPage';
import Travel from './components/Travel';
import CheckIn from './components/retreat/CheckIn';
import SettingsPage from './components/retreat/SettingsPage';
import TeamManagement from './components/retreat/TeamManagement';
import LedgerPage from './components/retreat/LedgerPage';
import ReportsPage from './components/retreat/ReportsPage';
import WaitlistPage from './components/retreat/WaitlistPage';
import FormsPage from './components/retreat/FormsPage';
import FormBuilder from './components/retreat/FormBuilder';
import FormSubmissions from './components/retreat/FormSubmissions';
import EmailPage from './components/retreat/EmailPage';
import PaymentsPage from './components/retreat/PaymentsPage';
import AuditLogPage from './components/retreat/AuditLogPage';
import VolunteersPage from './components/retreat/VolunteersPage';
import AttendeePortal from './components/retreat/AttendeePortal';
import { Toaster } from './components/ui/sonner';

// Global axios interceptor: kick admin out if the token expires server-side
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && localStorage.getItem('admin_token')) {
      // Only redirect for admin endpoints; portal endpoints are public
      const url = error.config?.url || '';
      if (url.includes('/api/retreat/') && !url.includes('/portal/') && !url.includes('/auth/login') && !url.includes('/auth/accept-invite')) {
        localStorage.removeItem('admin_token');
        if (!window.location.pathname.includes('/admin/login')) {
          window.location.href = '/admin/login?session=expired';
        }
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(() => !!localStorage.getItem('admin_token'));

  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token');
    if (adminToken) {
      setIsAdminAuthenticated(true);
    }
    // Clear the now-unused public-site gate flag so stale sessions don't linger
    sessionStorage.removeItem('rojo_authenticated');
  }, []);

  const handleAdminAuthenticate = (status) => {
    setIsAdminAuthenticated(status);
  };

  return (
    <div className="App">
      <Toaster />
      <ErrorBoundary>
        <BrowserRouter>
        <Routes>
          {/* Public marketing pages — fully open */}
          <Route path="/" element={<RetreatLanding />} />
          <Route path="/waitlist" element={<Waitlist />} />
          <Route path="/scholarship" element={<Scholarship />} />
          <Route path="/payment" element={<Payment />} />
          <Route path="/travel" element={<Travel />} />
          <Route path="/reserve" element={<Reserve />} />
          <Route path="/reservation/:code" element={<ReservationStatus />} />
          <Route path="/survey/:formId" element={<SurveyPage />} />
          
          {/* Admin login */}
          <Route path="/admin/login" element={
            isAdminAuthenticated ? (
              <Navigate to="/admin/retreat/dashboard" replace />
            ) : (
              <AdminLogin onAuthenticate={handleAdminAuthenticate} />
            )
          } />
          
          {/* Legacy admin dashboard redirect */}
          <Route path="/admin/dashboard" element={
            isAdminAuthenticated ? (
              <Navigate to="/admin/retreat/dashboard" replace />
            ) : (
              <Navigate to="/admin/login" replace />
            )
          } />
          
          {/* Retreat Admin Routes */}
          <Route path="/admin/retreat" element={
            isAdminAuthenticated ? (
              <AdminLayout />
            ) : (
              <Navigate to="/admin/login" replace />
            )
          }>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<RetreatDashboard />} />
            <Route path="attendees" element={<AttendeeManagement />} />
            <Route path="rooms" element={<RoomAssignment />} />
            <Route path="reservations" element={<ReservationsPage />} />
            <Route path="archive" element={<ArchivePage />} />
            <Route path="check-in" element={<CheckIn />} />
            <Route path="ledger" element={<LedgerPage />} />
            <Route path="donations" element={<LedgerPage />} />
            <Route path="refunds" element={<LedgerPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="waitlist" element={<WaitlistPage />} />
            <Route path="forms" element={<FormsPage />} />
            <Route path="forms/:formId" element={<FormBuilder />} />
            <Route path="forms/:formId/submissions" element={<FormSubmissions />} />
            <Route path="email" element={<EmailPage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="volunteers" element={<VolunteersPage />} />
            <Route path="audit-log" element={<AuditLogPage />} />
            <Route path="team" element={<TeamManagement />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>
          
          {/* Attendee login */}
          <Route path="/login" element={<AttendeeLogin />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Accept invite */}
          <Route path="/accept-invite" element={<AcceptInvite />} />
          
          {/* Attendee Portal - public access via unique link */}
          <Route path="/portal/:attendeeId" element={<AttendeePortal />} />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
